import { enumeratePlacements3D, cellKey, footprint, type Placement3D } from "./placements3d";
import type { Pack3DItem, Placed3DPiece } from "./types";

/**
 * Build the 3D deck-loading problem as a 0/1 integer program in CPLEX LP
 * format (the format HiGHS reads):
 *
 *   maximize    Σ value_p · x_p
 *   subject to  Σ_{p over cell} x_p ≤ 1            (no two boxes share a cell)
 *               Σ_{p of group g} x_p ≤ count_g     (each copy loaded at most once)
 *               x_p ≤ Σ_{q stackable, under tile} x_q   (full support: every
 *                 footprint tile of a raised box rests on a load-bearing box
 *                 directly below — no overhangs, no bridges, nothing on
 *                 non-stackable tops)
 *               x_p ∈ {0, 1}
 *
 * Identical items (same key, dims, value, stackability) are grouped into one
 * capacity row instead of one row per copy — copies are interchangeable, so
 * this removes branch-and-bound symmetry without changing the optimum.
 * Placements that can never be supported are pruned during enumeration.
 * Returns the LP string plus the tables needed to decode the solution.
 */
export function buildPacking3DLP(
  items: Pack3DItem[],
  cols: number,
  rows: number,
  maxH: number,
): { lp: string; placements: Placement3D[]; groups: number[][] } {
  // group identical copies; placements are enumerated once per group
  const groupOf = new Map<string, number>();
  const groups: number[][] = [];
  const reps: Pack3DItem[] = [];
  items.forEach((it, i) => {
    const sig = [it.key, it.w, it.d, it.h, it.value, it.stackable].join("|");
    let g = groupOf.get(sig);
    if (g === undefined) {
      g = groups.length;
      groupOf.set(sig, g);
      groups.push([]);
      reps.push(it);
    }
    groups[g].push(i);
  });

  const { placements, cellCover, itemPlace } = enumeratePlacements3D(reps, cols, rows, maxH);
  const P = placements.length;

  let lp = "\\ 3D deck packing (container loading) - MILP with support constraints (HiGHS)\n";
  lp += `\\ Deck ${cols}x${rows}, height ${maxH}, ${items.length} items in ${groups.length} groups, ${P} placements\n`;
  lp += "Maximize\n obj: " + placements.map((pl, p) => `${pl.value} x${p}`).join(" + ") + "\n";
  lp += "Subject To\n";
  for (const [k, ps] of cellCover) {
    if (ps.length > 1) {
      lp += ` cell_${k.replaceAll(",", "_")}: ${ps.map((p) => `x${p}`).join(" + ")} <= 1\n`;
    }
  }
  for (let g = 0; g < itemPlace.length; g++) {
    if (itemPlace[g].length > 0) {
      lp += ` item_${g}: ${itemPlace[g].map((p) => `x${p}`).join(" + ")} <= ${groups[g].length}\n`;
    }
  }
  for (let p = 0; p < P; p++) {
    const pl = placements[p];
    if (pl.z === 0) continue;
    for (const [x, y] of footprint(pl)) {
      const sup = (cellCover.get(cellKey(x, y, pl.z - 1)) ?? []).filter((q) => placements[q].stackable);
      // enumeratePlacements3D prunes unsupportable placements, so sup is never empty
      lp += ` sup_${p}_${x}_${y}: x${p} - ${sup.map((q) => `x${q}`).join(" - ")} <= 0\n`;
    }
  }
  lp += "Bounds\n";
  for (let p = 0; p < P; p++) lp += ` 0 <= x${p} <= 1\n`;
  lp += "Binary\n";
  for (let p = 0; p < P; p++) lp += ` x${p}\n`;
  lp += "End\n";

  return { lp, placements, groups };
}

/**
 * Decode a HiGHS solution back to placed pieces, fanning each group's chosen
 * placements out over that group's original (interchangeable) item indices.
 */
export function decodePacking3D(
  items: Pack3DItem[],
  placements: Placement3D[],
  groups: number[][],
  getPrimal: (name: string) => number,
): Placed3DPiece[] {
  const nextCopy = groups.map(() => 0);
  const out: Placed3DPiece[] = [];
  for (let p = 0; p < placements.length; p++) {
    if (getPrimal("x" + p) <= 0.5) continue;
    const pl = placements[p];
    const itemIndex = groups[pl.itemIdx][nextCopy[pl.itemIdx]++];
    out.push({
      itemIndex,
      key: items[itemIndex].key,
      value: pl.value,
      x: pl.x, y: pl.y, z: pl.z,
      w: pl.w, d: pl.d, h: pl.h,
      stackable: pl.stackable,
    });
  }
  return out;
}
